import { useCallback, useEffect, useRef, useState } from 'react';

import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { Strategy, StrategyStatus } from '~/api-services/AIStrategyBuilderService/types';
import StreamingService from '~/api-services/StreamingService';
import { StrategyEvent } from '~/api-services/StreamingService/types';

export type StrategyProgress = {
    percent: number | null;
    message: string;
    status: StrategyStatus | null;
    loading: boolean;
};

export type UseStrategyProgressOptions = {
    // Fetch the persisted status/percent here (default). Pass false when the caller already
    // loaded the strategy and hands it in via `initial`, to avoid a duplicate GET.
    seed?: boolean;
    initial?: Pick<Strategy, 'id' | 'status' | 'progress' | 'progress_percent'> | null;
};

/**
 * Tracks live processing progress for a single strategy: starts from the persisted
 * status/progress_percent (fetched here by default, or handed in via `options.initial`) so a
 * freshly opened in-progress strategy shows its last known %, then updates from realtime
 * `strategyEvent`s for that strategy id.
 */
export const useStrategyProgress = (strategyId: string, options?: UseStrategyProgressOptions): StrategyProgress => {
    const shouldSeed = options?.seed !== false;
    const initial = options?.initial ?? null;

    const [percent, setPercent] = useState<number | null>(null);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<StrategyStatus | null>(null);
    const [loading, setLoading] = useState(shouldSeed);

    // `initial` is applied once per strategy, and never over a live event — the caller's copy
    // is a snapshot from load time and would otherwise rewind the progress bar on re-render.
    const appliedInitialRef = useRef(false);
    const receivedEventRef = useRef(false);

    useEffect(() => {
        appliedInitialRef.current = false;
        receivedEventRef.current = false;
    }, [strategyId]);

    const seed = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await AIStrategyBuilderService.getStrategy(strategyId);
            if (data?.status && data?.returnData) {
                const s = data.returnData;
                setStatus(s.status);
                if (typeof s.progress_percent === 'number') {
                    setPercent(s.progress_percent);
                }
                if (typeof s.progress === 'string' && s.progress) {
                    setMessage(s.progress);
                }
            }
        } catch {
            // ignore — live events may still arrive
        } finally {
            setLoading(false);
        }
    }, [strategyId]);

    useEffect(() => {
        if (!shouldSeed) {
            return;
        }
        seed();
    }, [shouldSeed, seed]);

    useEffect(() => {
        if (!initial || initial.id !== strategyId || appliedInitialRef.current || receivedEventRef.current) {
            return;
        }
        appliedInitialRef.current = true;
        setStatus(initial.status);
        if (typeof initial.progress_percent === 'number') {
            setPercent(initial.progress_percent);
        }
        if (typeof initial.progress === 'string' && initial.progress) {
            setMessage(initial.progress);
        }
    }, [initial, strategyId]);

    useEffect(() => {
        const cb = (e: StrategyEvent) => {
            if (e.id !== strategyId) {
                return;
            }
            receivedEventRef.current = true;
            if (typeof e.progress === 'number') {
                setPercent(e.progress);
            }
            if (e.text) {
                setMessage(e.text);
            }
            if (e.status) {
                setStatus(e.status as StrategyStatus);
            }
        };
        StreamingService.subscribeStrategyEvent(cb);
        return () => {
            StreamingService.unsubscribeStrategyEvent(cb);
        };
    }, [strategyId]);

    return { percent, message, status, loading };
};
